import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronRight, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BookingBar, useIsMobile } from "@/components/booking/BookingBar";
import { RoomCard } from "@/components/rooms/RoomCard";
import { Container, Wide, Reveal, SectionTitle, Img, ParallaxImg, HScroll, Chip, EASE } from "@/components/common/Primitives";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useLang } from "@/lib/i18n";
import { useContent, minPriceForBuilding, minPrice } from "@/lib/content";
import { nok } from "@/lib/format";
import { usePageMeta } from "@/lib/seo";
import { srcSetFor, SIZES } from "@/lib/images";

/* ------------------------------------------------------------------ content */

const HOUSES = {
  fjordhotell: {
    img: { src: "/images/hotel-fjord.jpg", position: "60% 50%" },
    text: { nn: "Hovudhuset ved Fitjarvika, med restaurant, bar og terrasse.", en: "The main house on Fitjarvika, with restaurant, bar and terrace." },
  },
  elvafloy: {
    img: { src: "/images/autumn-river-cascades.jpg", position: "50% 45%" },
    text: { nn: "Rom ved Fitjarelva, om lag 200 meter frå Fjordhotellet. Her er hunden også velkomen.", en: "Rooms by the Fitjar river, about 200 metres from the Fjord Hotel. Dogs are welcome here too." },
  },
  midtfloy: {
    img: { src: "/images/hotel-entrance.jpg", position: "50% 40%" },
    text: { nn: "Einerom med eige bad, om lag 200 meter frå Fjordhotellet.", en: "Single rooms with private bathroom, about 200 metres from the Fjord Hotel." },
  },
};

const WALL = [
  { src: "/images/autumn-tree-fjord.jpg", nn: "Haust ved fjorden", en: "Autumn by the fjord", span: "col-span-2 row-span-2", position: "55% 50%" },
  { src: "/images/sunset-terrace.jpg", nn: "Kveld ved Fitjarvika", en: "Evening on Fitjarvika", span: "row-span-2", position: "50% 55%" },
  { src: "/images/fitjar-village-aerial.jpg", nn: "Fitjar sentrum frå lufta", en: "Fitjar village from above", position: "50% 55%" },
  { src: "/images/beach-volleyball-hotel.jpg", nn: "Sandvolleyball nedanfor hotellet", en: "Beach volleyball below the hotel", position: "50% 55%" },
  { src: "/images/kayak.jpg", nn: "Kajakk mellom Fitjarøyane", en: "Kayaking among the Fitjar islands", position: "50% 70%" },
  { src: "/images/fitjar-harbour-aerial.jpg", nn: "Hamna i Fitjar", en: "Fitjar harbour", span: "col-span-2", position: "50% 55%" },
  { src: "/images/mountain-view.jpg", nn: "Utsikt frå Varden", en: "The view from Varden", position: "50% 55%" },
  { src: "/images/nature-swan.jpg", nn: "Svane i fjøra", en: "Swan on the shore", position: "35% 50%" },
  { src: "/images/terrace-table.jpg", nn: "Terrassen ved Fjordhotellet", en: "The terrace at the Fjord Hotel", span: "col-span-2", position: "50% 55%" },
  { src: "/images/autumn-river-cascades.jpg", nn: "Fitjarelva ved Elvafløy", en: "The Fitjar river by the River Wing", position: "50% 55%" },
];

const EVENTS = [
  { id: "celebration", nn: ["Selskap", "Bryllaup, konfirmasjon eller rund dag? Me hjelper dykk med lokale, meny og overnatting."], en: ["Celebrations", "Wedding, confirmation or a big birthday? We help with the venue, menu and accommodation."], fallback: "/images/sunset-terrace.jpg" },
  { id: "memorial", nn: ["Minnesamvær", "Ei roleg samling etter gravferda. Me avtalar servering og tek hand om det praktiske på hotellet."], en: ["Memorial gatherings", "A quiet gathering after the funeral. We agree on the catering and take care of the practicalities at the hotel."], fallback: "/images/view-bay.jpg" },
  { id: "conference", nn: ["Kurs og konferanse", "Møterom og dagpakke med lunsj og kaffi, mellom Bergen og Haugesund."], en: ["Meetings and conferences", "Meeting rooms and a day package with lunch and coffee, between Bergen and Haugesund."], fallback: "/images/hotel-golden-hour.jpg" },
];

const DINING = [
  { nn: ["Komler på torsdagar", "Komlebuffé med tilbehør, kl. 13.00–18.00. Buffeen har sommarpause frå midten av juni til midten av august."], en: ["Komle on Thursdays", "Komle buffet with all the trimmings, 13:00–18:00. The buffet takes a summer break from mid-June to mid-August."] },
  { nn: ["Søndagsmiddag", "Familiebuffé med kalde og varme rettar og dessertbord, kl. 13.00–17.00. Bestill gjerne bord på førehand."], en: ["Sunday dinner", "Family buffet with cold and hot dishes and a dessert table, 13:00–17:00. Booking a table in advance is a good idea."] },
  { nn: ["Mat til selskapet", "Me set saman menyen etter kva de ønskjer å servere, og kor mange de blir."], en: ["Food for your party", "We put the menu together around what you would like to serve and how many you will be."] },
];

/* ------------------------------------------------------------------ hero */

const HERO_IMG = "/images/hotel-spring-river.jpg"; // desktop + tablet (landscape)
const HERO_IMG_MOBILE = "/images/hotel-summer-bloom.jpg"; // phones (portrait, rhododendrons)

const HERO_COPY = {
  kicker: ["Fitjar · mellom Bergen og Haugesund", "Fitjar · between Bergen and Haugesund"],
  title: ["Heilt i fjæresteinane.", "Right at the water’s edge."],
  sub: ["Familiedrive hotell ved Fitjarvika, med 61 rom i tre hus. Frukosten får du i restauranten ved sjøen.", "A family-run hotel on Fitjarvika with 61 rooms in three houses. Breakfast is served in the restaurant by the sea."],
};

const useUp = (reduce) => (delay = 0) => ({
  initial: reduce ? false : { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: EASE },
});

const TrustRow = () => {
  const { tx } = useLang();
  return (
    <Container className="mt-6 lg:mt-7">
      <div className="flex flex-col gap-1.5 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between" data-testid="home-trust-row">
        <p className="font-medium text-foreground">{tx("Frukost inkludert · Gratis parkering · Betal på hotellet", "Breakfast included · Free parking · Pay at the hotel")}</p>
        <p>
          {tx("Gratis avbestilling fram til kl. 18.00 dagen før ankomst.", "Free cancellation until 18:00 the day before arrival.")}{" "}
          <Link to="/praktisk#vilkar" className="underline decoration-border underline-offset-4 transition-colors hover:text-foreground hover:decoration-foreground" data-testid="home-terms-link">{tx("Sjå vilkåra", "See the terms")}</Link>
        </p>
      </div>
    </Container>
  );
};

/**
 * Desktop (>= 1024px). The photo fills the first screen with the header floating transparent on
 * top; the sky is left untouched and only the lower part gets a soft scrim so the copy reads.
 * Copy sits bottom-left in a moderate size (always one line), the search bar straddles the bottom
 * edge of the photo like a product element, and the facts line sits quietly on white beneath it.
 * Motion: slow settle-zoom on load, gentle parallax + copy fade on scroll – nothing else.
 */
const DesktopHero = () => {
  const { tx } = useLang();
  const reduce = useReducedMotion();
  const up = useUp(reduce);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "12%"]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const facts = [tx("Frukost inkludert", "Breakfast included"), tx("Gratis parkering", "Free parking"), tx("Betal på hotellet", "Pay at the hotel")];

  return (
    <section className="relative" data-testid="home-hero">
      <div ref={ref} className="relative h-[calc(100svh-96px)] max-h-[900px] min-h-[600px] overflow-hidden bg-[hsl(214,18%,16%)]" data-testid="home-hero-photo">
        <motion.div style={{ y: photoY }} className="absolute inset-x-0 -top-[8%] h-[116%] will-change-transform">
          <motion.div initial={reduce ? false : { scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 7, ease: [0.16, 1, 0.3, 1] }} className="h-full w-full origin-center">
            <picture className="block h-full w-full">
              <source type="image/webp" srcSet={srcSetFor(HERO_IMG)} sizes="100vw" />
              <img
                src={HERO_IMG}
                alt={tx("Fitjar Fjordhotell ved Fitjarvika ein vårdag", "Fitjar Fjordhotell on Fitjarvika on a spring day")}
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover object-[50%_64%]"
              />
            </picture>
          </motion.div>
        </motion.div>
        <div className="scrim-nav pointer-events-none absolute inset-x-0 top-0 h-40" aria-hidden />
        <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-[64%]" aria-hidden />

        <Container className="relative flex h-full flex-col justify-end pb-[5.75rem]">
          <motion.div style={{ y: copyY, opacity: copyOpacity }} className="flex items-end justify-between gap-12">
            <div className="min-w-0">
              <motion.p {...up(0.06)} className="hero-shadow text-[0.9375rem] font-medium text-white/80" data-testid="home-hero-kicker">{tx(...HERO_COPY.kicker)}</motion.p>
              {/* Fluid size and nowrap: one confident line inside the 1200px container at every desktop width */}
              <motion.h1 {...up(0.12)} className="hero-shadow mt-3 whitespace-nowrap text-[clamp(2.75rem,4vw,4.5rem)] font-semibold leading-[0.98] tracking-[-0.035em] text-white" data-testid="home-hero-title">{tx(...HERO_COPY.title)}</motion.h1>
              <motion.p {...up(0.2)} className="hero-shadow mt-4 max-w-[36rem] text-lg leading-relaxed text-white/85" data-testid="home-hero-sub">{tx(...HERO_COPY.sub)}</motion.p>
            </div>
            <motion.p {...up(0.26)} className="hero-shadow hidden shrink-0 pb-1.5 text-right text-sm leading-snug text-white/70 xl:block" data-testid="home-hero-caption">
              {tx("Fjordhotellet ein vårdag", "The Fjord Hotel on a spring day")}
              <br />
              {tx("61 rom · tre hus · frukost ved sjøen", "61 rooms · three houses · breakfast by the sea")}
            </motion.p>
          </motion.div>
        </Container>
      </div>

      {/* Search – straddles the bottom edge of the photo; facts sit quietly on white beneath it */}
      <Container className="relative z-10 -mt-[38px]">
        <motion.div {...up(0.32)} className="w-full max-w-[1040px]" data-sticky-anchor data-testid="home-booking-strip">
          <BookingBar variant="hero" />
        </motion.div>
        <motion.div {...up(0.44)} className="mt-5 flex max-w-[1040px] flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-muted-foreground" data-testid="home-trust-row">
          {facts.map((t, i) => (
            <span key={t} className="flex items-center gap-x-2.5">
              {i > 0 && <span className="h-1 w-1 rounded-full bg-border" aria-hidden />}
              <span className="font-medium text-foreground">{t}</span>
            </span>
          ))}
          <span className="h-1 w-1 rounded-full bg-border" aria-hidden />
          <span>
            {tx("Gratis avbestilling fram til kl. 18.00 dagen før ankomst.", "Free cancellation until 18:00 the day before arrival.")}{" "}
            <Link to="/praktisk#vilkar" className="underline decoration-border underline-offset-4 transition-colors hover:text-foreground hover:decoration-foreground" data-testid="home-terms-link">{tx("Sjå vilkåra", "See the terms")}</Link>
          </span>
        </motion.div>
      </Container>
    </section>
  );
};

/**
 * Phones + tablets (< 1024px): copy on white, then the photo in a tall rounded frame with a slow
 * settle-zoom and gentle parallax; the booking card overlaps the bottom edge of the photo.
 * Phones get the portrait summer photo (rhododendrons), tablets the landscape spring photo.
 */
const MobileHero = () => {
  const { tx } = useLang();
  const reduce = useReducedMotion();
  const up = useUp(reduce);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], ["-4%", reduce ? "-4%" : "4%"]);

  return (
    <section className="relative" data-testid="home-hero">
      <Container className="pt-7 sm:pt-10">
        <motion.p {...up(0.04)} className="text-sm font-medium text-muted-foreground" data-testid="home-hero-kicker">{tx(...HERO_COPY.kicker)}</motion.p>
        <motion.h1 {...up(0.1)} className="mt-3 max-w-[14ch] text-[2.625rem] font-semibold leading-[0.95] tracking-[-0.035em] text-foreground sm:text-[3.5rem]" data-testid="home-hero-title">{tx(...HERO_COPY.title)}</motion.h1>
        <motion.p {...up(0.16)} className="mt-4 max-w-xl text-[1.0625rem] leading-relaxed text-muted-foreground sm:text-lg" data-testid="home-hero-sub">{tx(...HERO_COPY.sub)}</motion.p>
      </Container>

      <Wide className="mt-7 sm:mt-9">
        <motion.div ref={ref} {...up(0.22)} className="relative overflow-hidden rounded-[26px] bg-muted sm:rounded-[28px]" data-testid="home-hero-photo">
          <div className="aspect-[4/5] sm:aspect-[16/10]" aria-hidden />
          <motion.div style={{ y: photoY }} className="absolute inset-x-0 -top-[5%] h-[110%] will-change-transform">
            <motion.div initial={reduce ? false : { scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 6.5, ease: [0.16, 1, 0.3, 1] }} className="h-full w-full origin-center">
              <picture className="block h-full w-full">
                <source media="(max-width: 639px)" type="image/webp" srcSet={srcSetFor(HERO_IMG_MOBILE)} sizes="100vw" />
                <source type="image/webp" srcSet={srcSetFor(HERO_IMG)} sizes="100vw" />
                <img
                  src={HERO_IMG_MOBILE}
                  alt={tx("Fitjar Fjordhotell med rhododendron i blom", "Fitjar Fjordhotell with rhododendrons in bloom")}
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-cover object-[50%_55%] sm:object-[50%_70%]"
                />
              </picture>
            </motion.div>
          </motion.div>
        </motion.div>
      </Wide>

      <Container className="relative z-10 -mt-9 sm:-mt-10">
        <motion.div {...up(0.3)} className="mx-auto w-full sm:max-w-[640px]" data-sticky-anchor data-testid="home-booking-strip">
          <BookingBar variant="hero" />
        </motion.div>
      </Container>
      <TrustRow />
    </section>
  );
};

const Hero = () => (useIsMobile() ? <MobileHero /> : <DesktopHero />);

/* ------------------------------------------------------------------ photo wall */

const WallItem = ({ item, i }) => {
  const { tx } = useLang();
  const label = tx(item.nn, item.en);
  const range = [6, 4, 5, 3, 6, 4, 5, 3, 6, 4][i % 10];
  return (
    <Reveal as="figure" delay={Math.min(i, 6) * 0.04} className={`group img-zoom relative overflow-hidden rounded-[16px] bg-muted sm:rounded-[20px] ${item.span || ""}`} data-testid={`home-wall-item-${i}`}>
      <ParallaxImg src={item.src} alt={label} className="absolute inset-0 h-full w-full" radius="rounded-none" position={item.position} range={range} sizes={item.span?.includes("col-span-2") ? SIZES.half : SIZES.quarter} />
      <figcaption className="pointer-events-none absolute bottom-3 left-3 translate-y-1 rounded-full bg-white/92 px-3 py-1 text-xs font-semibold text-foreground opacity-0 backdrop-blur transition-[opacity,transform] duration-200 group-hover:translate-y-0 group-hover:opacity-100">
        {label}
      </figcaption>
    </Reveal>
  );
};

/* ------------------------------------------------------------------ page */

export default function Home() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const phone = useIsMobile(768);
  usePageMeta(
    tx("Fitjar Fjordhotell – heilt i fjæresteinane", "Fitjar Fjordhotell – right at the water’s edge"),
    tx("Familiedrive hotell ved Fitjarvika, med 61 rom i tre hus. Frukost inkludert, betal på hotellet.", "Family-run hotel on Fitjarvika with 61 rooms in three houses. Breakfast included, pay at the hotel.")
  );
  const buildings = data?.buildings || [];
  const roomTypes = data?.room_types || [];
  const byBuilding = Object.fromEntries(buildings.map((b) => [b.id, b]));
  const eventImages = Object.fromEntries((data?.event_types || []).map((e) => [e.id, e.image]));
  const seaRooms = roomTypes.filter((r) => (r.badges || []).includes("sea_view"));
  const seaFrom = seaRooms.length ? Math.min(...seaRooms.map((r) => minPrice(r)).filter(Boolean)) : null;

  const questions = lang === "en"
    ? ["Can I bring my dog?", "Which room suits two people?", "When do you serve komle?", "Do you have step-free rooms?"]
    : ["Kan eg ta med hund?", "Kva rom passar for to?", "Når serverer de komler?", "Har de rom utan trapper?"];

  const [filter, setFilter] = useState("all");
  const shownRooms = filter === "all" ? roomTypes : roomTypes.filter((r) => r.building === filter);

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Statement */}
      <section className="pt-20 lg:pt-32" data-testid="home-statement">
        <Container>
          <Reveal className="grid gap-6 lg:grid-cols-12 lg:gap-10">
            <p className="text-sm font-semibold text-muted-foreground lg:col-span-3 lg:pt-2">{tx("Fitjar Fjordhotell · sidan 1986", "Fitjar Fjordhotell · since 1986")}</p>
            <div className="lg:col-span-9">
              <p className="max-w-[26ch] text-[1.625rem] leading-[1.2] tracking-[-0.025em] text-foreground sm:text-[2rem] lg:text-[2.625rem]" data-testid="home-statement-text">
                {tx("Hotellet ligg heilt nede ved sjøen i Fitjar, med Fitjarøyane rett utanfor. Her bur du enkelt og godt, og et frukost med utsikt over Fitjarvika.", "The hotel sits right down by the sea in Fitjar, with the Fitjar islands just outside. Stay simply and well, and have breakfast with a view over Fitjarvika.")}
              </p>
              <dl className="mt-10 grid grid-cols-3 gap-6 border-t border-border pt-7 lg:mt-12 lg:gap-10" data-testid="home-facts">
                {[
                  ["61", tx("rom i tre hus", "rooms in three houses")],
                  ["1986", tx("familiedrive sidan", "family-run since")],
                  ["Gratis", tx("parkering og avbestilling", "parking and cancellation")],
                ].map(([v, l]) => (
                  <div key={l} className="flex flex-col gap-2">
                    <dt className="order-2 text-sm leading-snug text-muted-foreground">{l}</dt>
                    <dd className="text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-foreground tabular sm:text-[2.25rem]">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Rooms – filter by house; swipeable row on phones, 3-column grid from tablet up */}
      <section className="pt-20 lg:pt-32" data-testid="home-rooms">
        <Container>
          <SectionTitle
            size="lg"
            title={tx("Finn rommet ditt", "Find your room")}
            sub={tx("Vel sjøutsikt i Fjordhotellet, eit rom ved Fitjarelva eller eit einerom på Midtfløy. Frukost er inkludert, same kva rom du vel.", "Choose a sea view in the Fjord Hotel, a room by the Fitjar river or a single room in the Middle Wing. Breakfast is included whichever room you choose.")}
            action={tx("Sjå alle rom", "See all rooms")}
            to="/rom"
            testid="home-rooms-title"
          />
          <div className="no-scrollbar -mx-5 mt-8 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:px-0" role="tablist" aria-label={tx("Vel hus", "Choose house")} data-testid="home-rooms-filter">
            <Chip active={filter === "all"} onClick={() => setFilter("all")} testid="home-rooms-filter-all">{tx("Alle rom", "All rooms")}</Chip>
            {buildings.map((b) => (
              <Chip key={b.id} active={filter === b.id} onClick={() => setFilter(b.id)} testid={`home-rooms-filter-${b.id}`}>{tr(b.name)}</Chip>
            ))}
          </div>
          {phone ? (
            <HScroll
              key={filter}
              className="mt-7"
              testid="home-rooms-scroller"
              items={shownRooms.map((rt) => ({ ...rt, w: "w-[78vw] max-w-[340px]" }))}
              renderItem={(rt, i) => <RoomCard rt={rt} building={byBuilding[rt.building]} testid={`home-room-card-${rt.id}`} eager={i < 2} />}
            />
          ) : (
            <motion.div layout className="mt-8 grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-7" data-testid="home-rooms-grid">
              {shownRooms.map((rt, i) => (
                <motion.div key={rt.id} layout initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: (i % 3) * 0.05, ease: EASE }}>
                  <RoomCard rt={rt} building={byBuilding[rt.building]} testid={`home-room-card-${rt.id}`} eager={i < 3} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </Container>
      </section>

      {/* Cinematic band – the real view over Fitjarvika from the hotel (portrait crop on phones) */}
      <section className="relative mt-20 h-[78svh] max-h-[860px] min-h-[520px] overflow-hidden bg-[hsl(214,18%,16%)] lg:mt-32" data-testid="home-chair-band">
        <ParallaxImg src="/images/view-bay-panorama.jpg" mobileSrc="/images/view-bay.jpg" alt={tx("Utsikt over Fitjarvika frå hotellet", "The view over Fitjarvika from the hotel")} className="absolute inset-0 h-full w-full" radius="rounded-none" position="50% 60%" mobilePosition="50% 45%" range={7} sizes={SIZES.full} />
        <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-[70%]" aria-hidden />
        <Container className="relative flex h-full flex-col justify-end pb-10 lg:pb-16">
          <Reveal className="max-w-3xl">
            <p className="hero-shadow text-[0.9375rem] font-semibold text-white">{tx("Fjordhotellet · superiorrom", "The Fjord Hotel · superior rooms")}</p>
            <h2 className="hero-shadow mt-3 max-w-[14ch] text-[2.25rem] font-semibold leading-[0.98] tracking-[-0.035em] text-white sm:text-[3rem] lg:text-[3.5rem]">{tx("Vakn med Fitjarvika utanfor vindauget", "Wake up with Fitjarvika outside your window")}</h2>
            <p className="hero-shadow mt-4 max-w-md text-[1.0625rem] leading-relaxed text-white/90 sm:text-lg">
              {tx("Superiorrom med sjøutsikt i Fjordhotellet. Frukost inkludert.", "Superior rooms with a sea view in the Fjord Hotel. Breakfast included.")}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-5 lg:mt-8">
              {seaFrom && (
                <div className="hero-shadow">
                  <p className="text-sm text-white/75">{tx("Frå", "From")}</p>
                  <p className="text-[1.75rem] font-bold leading-none tracking-tight text-white tabular" data-testid="home-seaview-price">{nok(seaFrom, lang)} <span className="text-base font-medium text-white/75">/ {tx("natt", "night")}</span></p>
                </div>
              )}
              <Button asChild size="lg" variant="inverse" data-testid="home-chair-band-cta">
                <Link to="/rom#fjordhotell">{tx("Sjå rom med sjøutsikt", "See sea-view rooms")} <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </Reveal>
        </Container>
      </section>

      {/* Three houses – compact rows on phones, photo cards from tablet up */}
      <section className="pt-20 lg:pt-32" data-testid="home-welcome">
        <Container>
          <SectionTitle size="lg" title={tx("Kvar vil du bu?", "Where would you like to stay?")} sub={tx("Dei 61 romma er fordelte på tre hus. Alle gjester et frukost i Fjordhotellet ved sjøen.", "The 61 rooms are spread across three houses. All guests have breakfast in the Fjord Hotel by the sea.")} testid="home-buildings-title" />
          {phone ? (
            <Reveal className="mt-7 divide-y divide-border border-y border-border">
              {buildings.map((b) => {
                const from = minPriceForBuilding(roomTypes, b.id);
                const count = roomTypes.filter((r) => r.building === b.id).length;
                const h = HOUSES[b.id] || { img: { src: b.image }, text: b.tagline };
                return (
                  <Link key={b.id} to={`/rom#${b.id}`} className="group flex items-center gap-4 py-4 active:bg-secondary/60" data-testid={`home-building-${b.id}`}>
                    <Img src={h.img.src} alt={tr(b.name)} className="img-zoom h-[88px] w-[88px] shrink-0" radius="rounded-[18px]" position={h.img.position} sizes="88px" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[1.0625rem] font-semibold leading-tight tracking-[-0.015em] text-foreground">{tr(b.name)}</span>
                      <span className="mt-1 line-clamp-2 block text-sm leading-snug text-muted-foreground">{tr(h.text)}</span>
                      <span className="mt-1.5 block text-sm text-foreground tabular">
                        {count} {tx(count === 1 ? "romtype" : "romtypar", count === 1 ? "room type" : "room types")}
                        {from && <> · {tx("frå", "from")} <span className="font-semibold">{nok(from, lang)}</span></>}
                      </span>
                    </span>
                    <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                  </Link>
                );
              })}
            </Reveal>
          ) : (
            <div className="mt-10 grid gap-x-7 gap-y-10 md:grid-cols-3">
              {buildings.map((b, i) => {
                const from = minPriceForBuilding(roomTypes, b.id);
                const count = roomTypes.filter((r) => r.building === b.id).length;
                const h = HOUSES[b.id] || { img: { src: b.image }, text: b.tagline };
                return (
                  <Reveal key={b.id} delay={i * 0.05}>
                    <Link to={`/rom#${b.id}`} className="group block rounded-[22px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4" data-testid={`home-building-${b.id}`}>
                      <Img src={h.img.src} alt={tr(b.name)} className="img-zoom aspect-[4/3]" radius="rounded-[22px]" position={h.img.position} sizes={SIZES.third} />
                      <div className="mt-5 flex items-start justify-between gap-4">
                        <h3 className="text-[1.25rem] leading-tight tracking-[-0.02em] text-foreground">{tr(b.name)}</h3>
                        <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
                      </div>
                      <p className="mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">{tr(h.text)}</p>
                      <p className="mt-3 text-[0.9375rem] text-foreground tabular">
                        {count} {tx(count === 1 ? "romtype" : "romtypar", count === 1 ? "room type" : "room types")}
                        {from && <> · {tx("frå", "from")} <span className="font-semibold">{nok(from, lang)}</span> {tx("per natt", "per night")}</>}
                      </p>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          )}
        </Container>
      </section>

      {/* Photo wall – tall swipe cards on phones, dense grid from tablet up */}
      <section className="mt-20 bg-secondary/70 py-20 lg:mt-32 lg:py-32" data-testid="home-experience">
        <Container>
          <SectionTitle size="lg" title={tx("Ta ein tur ut", "Head outside")} sub={tx("Ta ein tur opp til Varden, spel sandvolleyball i fjøra eller finn ein plass ved sjøen.", "Walk up to Varden, play beach volleyball by the shore or find a spot by the sea.")} testid="home-experience-title" />
        </Container>
        {phone ? (
          <Container className="mt-8">
            <HScroll
              testid="home-experience-gallery"
              items={WALL.map((w) => ({ ...w, w: "w-[68vw] max-w-[300px]" }))}
              renderItem={(item, i) => (
                <figure data-testid={`home-wall-item-${i}`}>
                  <Img src={item.src} alt={tx(item.nn, item.en)} className="img-zoom aspect-[4/5]" radius="rounded-[20px]" position={item.position} sizes="70vw" />
                  <figcaption className="mt-3 text-sm font-medium text-foreground">{tx(item.nn, item.en)}</figcaption>
                </figure>
              )}
            />
          </Container>
        ) : (
          <Wide className="mt-10">
            <div className="grid grid-flow-dense grid-cols-2 auto-rows-[150px] gap-2 sm:auto-rows-[200px] sm:gap-3 lg:grid-cols-4 lg:auto-rows-[240px] lg:gap-4" data-testid="home-experience-gallery">
              {WALL.map((item, i) => <WallItem key={item.src + i} item={item} i={i} />)}
            </div>
          </Wide>
        )}
      </section>

      {/* Dining */}
      <section className="py-20 lg:py-32" data-testid="home-dining">
        <Container className="grid gap-8 lg:grid-cols-12 lg:items-center lg:gap-16">
          <Reveal className="lg:col-span-6">
            <ParallaxImg src="/images/restaurant-table.jpg" alt={tx("Dekka bord ved vindauget", "A set table by the window")} className="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]" radius="rounded-[24px] sm:rounded-[28px]" range={5} sizes={SIZES.half} />
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="text-[2rem] leading-[1.02] tracking-[-0.03em] sm:text-[2.5rem] lg:text-[2.75rem]">{tx("Kom innom til middag", "Join us for dinner")}</h2>
              <p className="mt-4 max-w-[52ch] text-lg leading-relaxed text-muted-foreground">{tx("I restauranten ved Fitjarvika serverer me komler på torsdagar og familiebuffé på søndagar. Me lagar også mat til selskap.", "In the restaurant on Fitjarvika we serve komle on Thursdays and a family buffet on Sundays. We also cater for parties.")}</p>
            </Reveal>
            <Reveal delay={0.06} className="mt-8 divide-y divide-border border-y border-border" data-testid="home-dining-rhythm">
              {DINING.map((d) => {
                const [t, body] = lang === "en" ? d.en : d.nn;
                return (
                  <div key={t} className="grid gap-1 py-5 sm:grid-cols-[11rem_1fr] sm:gap-6">
                    <p className="text-[1.0625rem] font-semibold text-foreground">{t}</p>
                    <p className="text-[0.9375rem] leading-relaxed text-muted-foreground">{body}</p>
                  </div>
                );
              })}
            </Reveal>
            <Button asChild variant="outline" size="lg" className="mt-8" data-testid="home-dining-cta"><Link to="/mat">{tx("Mat og opningstider", "Dining and opening hours")}</Link></Button>
          </div>
        </Container>
      </section>

      {/* Events */}
      <section className="pb-20 lg:pb-32" data-testid="home-events">
        <Container>
          <SectionTitle size="lg" title={tx("Samlast ved Fitjarvika", "Gather on Fitjarvika")} sub={tx("Me har lokale til selskap, minnesamvær og møte. Treng gjestene overnatting, har me 61 rom i dei tre husa våre.", "We have rooms for parties, memorial gatherings and meetings. If your guests need to stay over, we have 61 rooms in our three houses.")} action={tx("Sjå lokale og moglegheiter", "See venues and options")} to="/arrangement" testid="home-events-title" />
          <div className="mt-8 grid gap-x-7 gap-y-8 md:mt-10 md:grid-cols-3 md:gap-y-10">
            {EVENTS.map((e, i) => {
              const [t, body] = lang === "en" ? e.en : e.nn;
              return (
                <Reveal key={e.id} delay={i * 0.05}>
                  <Link to={`/arrangement?type=${e.id}#forespurnad`} className="group block rounded-[22px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4" data-testid={`home-event-${e.id}`}>
                    <Img src={eventImages[e.id] || e.fallback} alt={t} className="img-zoom aspect-[16/10] md:aspect-[4/3]" radius="rounded-[22px]" sizes={SIZES.third} />
                    <h3 className="mt-4 text-[1.25rem] leading-tight tracking-[-0.02em] text-foreground md:mt-5">{t}</h3>
                    <p className="mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">{body}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground underline decoration-border underline-offset-[6px] transition-colors group-hover:decoration-foreground">{tx("Send førespurnad", "Send an enquiry")} <ArrowRight className="h-4 w-4" /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Fjordverten – ink panel */}
      <section className="pb-20 lg:pb-32" data-testid="home-concierge">
        <Container>
          <Reveal className="relative overflow-hidden rounded-[28px] bg-primary px-6 py-10 text-primary-foreground sm:px-10 sm:py-12 lg:rounded-[36px] lg:px-14 lg:py-16">
            <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-6">
                <p className="text-sm font-semibold text-white/60">{tx("Fjordverten · svarar døgnet rundt", "Fjordverten · answers around the clock")}</p>
                <h2 className="mt-3 text-[2rem] leading-[1.0] tracking-[-0.035em] text-white sm:text-[2.5rem] lg:text-[3rem]">{tx("Spør oss om opphaldet", "Ask us about your stay")}</h2>
                <p className="mt-4 max-w-[48ch] text-[1.0625rem] leading-relaxed text-white/70 sm:text-lg">{tx("Fjordverten er den digitale assistenten vår. Han svarar på spørsmål om rom, mat og praktisk informasjon heile døgnet. Du kan også ringje resepsjonen på 53 45 77 00.", "Fjordverten is our digital assistant. It answers questions about rooms, food and practical information around the clock. You can also call reception on +47 53 45 77 00.")}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button size="lg" variant="inverse" onClick={() => ask(null)} data-testid="home-concierge-open"><MessageCircle className="h-4 w-4" /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
                  <Button asChild size="lg" variant="ghost-light" className="border border-white/20 tabular" data-testid="home-call-cta"><a href="tel:+4753457700"><Phone className="h-4 w-4" /> 53 45 77 00</a></Button>
                </div>
              </div>
              <div className="lg:col-span-5 lg:col-start-8">
                <p className="text-sm font-semibold text-white/60">{tx("Forslag til spørsmål", "Suggested questions")}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {questions.map((q, i) => (
                    <button key={q} type="button" onClick={() => ask(q)} data-testid={`home-concierge-chip-${i}`} className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/[0.14]">{q}</button>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
